import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ISemester } from '../../../models/semesters.model';
import { doGetCoursesInSemester } from '../../../store/class.slice';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import { useSnackbar } from 'notistack';
import { Select } from 'antd';
import Button from '../../Button';
import Loader from '../../Loader';
import {
  infoVariant,
  warningVariant,
  errorVariant,
} from '../../../lib/barVariant';
import {
  IClass,
  IClassInfoReq2,
  IClassReq2,
  IClassSubjectReq,
  ICourse,
} from '../../../models/campusClasses.model';
import SelectTeacherModal from '../../modals/SelectTeacherModal';
import { IStaff } from '../../../models/employees.model';
import { doGetCampusClassrooms } from '../../../store/classroom.slice';
import ClassTimetable2 from './Timetable2';
import QueryString from 'qs';
import useAuth from '../../../hooks/useAuth';
import useNotification from '../../../hooks/useNotification';
import useAxios from '../../../hooks/useAxios';
import { IGetSemesterCoursesSubject } from '../../../models/semesterCoursesSubject.model';
import { getSemesterCoursesSubject } from '../../../api/semesterCoursesSubject.api';
import { isEqual } from 'lodash';
import SelectEduStep from '../../SelectEduStep';
import SemesterTypeSelect from '../../common/SemesterTypeSelect';
import { SEMESTER_TYPE } from '../../../constans';
import MonthSelect from '../../common/MonthSelect';
import DateRangePicker from '../../common/DateRangePicker';
import UseUtil from '../../../hooks/useUtil';

const { Option } = Select;

interface IProps {
  classData?: IClass | null;
  submit: any;
}

const ClassEditor2: FC<IProps> = ({
  classData,
  submit,
}) => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { infoNotification } = useNotification();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const dispatch = useDispatch();
  const { currentCampus, currentSemesterCode } = useSelector(
    (state: RootState) => state.auth,
  );
  const { data: res, execute } = useAxios<IGetSemesterCoursesSubject>();

  // 공통 코드
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // CL0002 = 수업시간(교시당)
  const CL0002 = commonCodes.find(
    (item) => item.commonCode === 'CL0002',
  )?.detailCode;
  // SY0001 = 요일
  const SY0001 = commonCodes.find(
    (item) => item.commonCode === 'SY0001',
  )?.detailCode;

  // PR0002 = 학기 유형
  const PR0002 = commonCodes.find(
    (item) => item.commonCode === 'PR0002',
  )?.detailCode;

  // 학기 목록
  const { semesters, isLoading: semesterLoading } = useSelector(
    (state: RootState) => state.semester,
  );
  // 과정 목록
  const { courses, isLoading: courseLoading } = useSelector(
    (state: RootState) => state.class,
  );
  // 강의실 목록
  const { classrooms, isLoading: classroomLoading } = useSelector(
    (state: RootState) => state.classroom,
  );

  const { REGULAR, SPECIAL_CLASS } = SEMESTER_TYPE;
  const initialRowState = {
    monthCode: '1',
    startDate: null,
    endDate: null,
    tuition: 0,
  }

  const [data, setData] = useState<any>();
  const [campus, setCampus] = useState<string>();
  const [className, setClassName] = useState('');
  const [semester, setSemester] = useState('');
  const [eduStepCode, setEduStepCode] = useState<string>();
  const [course, setCourse] = useState<string>();
  const [courseInfo, setCourseInfo] = useState<any>();
  const [selectTeacherVisible, setSelectTeacherVisible] = useState(false);
  const [teacherInputRef, setTeacherInputRef] = useState<any>();
  const [teacher, setTeacher] = useState<any>();
  const [lessonTime, setLessonTime] = useState<string>();
  const [minPrice, setMinPrice] = useState('');
  const [monthTuition1, setMonthTuition1] = useState('');
  const [monthTuition2, setMonthTuition2] = useState('');
  const [monthTuition3, setMonthTuition3] = useState('');
  const [scheduleInput, setScheduleInput] = useState<any>([]);
  const [dayList, setDayList] = useState<any>([]);
  const [classroomList, setClassroomList] = useState<any>([]);
  const [regYn, setRegYn] = useState('N');

  const [semesterType, setSemesterType] = useState(REGULAR);
  const [srtDate, setSrtDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [month, setMonth] = useState<number>(1);
  const [rows, setRows] = useState<any>([initialRowState]);

  const filteredSemesters = useMemo(() => {
    return semesters?.filter(semester => semester.semesterType === semesterType);
  }, [semesterType]);

  useEffect(() => {
    setSemester('');
    setCourse(undefined);
    setCourseInfo(undefined);
    setEduStepCode(undefined);
  }, [semesterType])

  useEffect(() => {
    if (course) {
      const currentCourse = courses?.find(
        (item) => item.semesterCourseCode === Number(course),
      );
      setCourseInfo(currentCourse);
      execute(getSemesterCoursesSubject({ semesterCourseCode: +course }));
    }
  }, [course, courses]);

  useEffect(() => {
    if (SY0001) {
      const list: any = [];
      SY0001.forEach((item: any) => {
        list.push({
          label: item.korName,
          value: item.detailCode,
        });
      });
      // 주말(토일) 제외
      list.splice(6, 1);
      list.splice(0, 1);
      setDayList(list);
    }
  }, [commonCodes]);

  useEffect(() => {
    if (classrooms) {
      const _classrooms: any = [];
      classrooms[0]?.classroom.forEach((item: any) =>
        _classrooms.push({
          label: item.classroomName,
          value: item.classroomCode,
        }),
      );
      setClassroomList(_classrooms);
    }
  }, [classrooms]);

  useEffect(() => {
    if (data) {
      if (!currentSemesterCode) {
        getCourses(data.semesterCode);
      }
      getClassrooms(data.campusCode);
      setCampus(data.campusCode);
      setClassName(data.className);
      setSemester(data.semesterCode);
      setEduStepCode(data.eduStepCode);
      setLessonTime(data.lessonTime);
      setCourse(data.semesterCourseCode);
      setMinPrice(data.minPrice);

      setSemesterType(data.semesterType);
      if (data.semesterType === REGULAR) {
        setMonthTuition1(data.tuition[0].tuition);
        setMonthTuition2(data.tuition[1].tuition);
        setMonthTuition3(data.tuition[2].tuition);
        setTeacher({
          memberCode: data.teacherCode,
          memberName: data.teacherKorName,
        });
      }

      if (data.semesterType === SPECIAL_CLASS) {
        setMonth(data.tuition.length);
        setRows(data.tuition)
        setSrtDate(data.classSrtDate);
        setEndDate(data.classEndDate);
        setTeacher({
          memberCode: data.teacherCode,
          memberName: data.teacherKoreaName,
        });
      }

      if (data.regYn) {
        setRegYn(data.regYn);
      }

      const input = data.classInfo?.map((item: any, index: number) => {
        return {
          day: item.lessonWeekday,
          classes: item.classTimes.map((subitem: any, subIndex: number): any => {
            return {
              id: `${index}-${subIndex}`,
              classTimeCode: subitem.classTimeCode,
              classTimeSeq: subitem.classTimeSeq,
              startTime: subitem.srtTime,
              classroom: subitem.classRoomCode,
              teacher: {
                memberCode: subitem.teacherCode,
                memberName: subitem.teacherKorName,
              },
              subjectCode: subitem.subjectCode,
              subjectName: subitem.subjectName,
            };
          }),
        };
      });

      setScheduleInput(input);
    } else if (data === null) {
      getClassrooms(currentCampus);
      setCampus(currentCampus);
      setSemester('');
      //setClassName('');
      setCourse(undefined);
      setCourseInfo(undefined);
      setLessonTime(undefined);
      setMinPrice('');
      setTeacher(undefined);
      setMonthTuition1('');
      setMonthTuition2('');
      setMonthTuition3('');
      setRegYn('N');
      setScheduleInput([]);
    }
  }, [data, semesterType]);

  useEffect(() => {
    if (query.tab === 'createClass') {
      setData(null);
    } else {
      setData(classData);
    }
  }, [classData, query.tab]);

  useEffect(() => {
    setCampus(currentCampus);
  }, [currentCampus]);

  useEffect(() => {
    if (currentSemesterCode) {
      setSemester(String(currentSemesterCode));
      getCourses(currentSemesterCode);
    }
  }, [currentSemesterCode]);

  const getCourses = async (semesterCode: any) => {
    const res = await dispatch(doGetCoursesInSemester(semesterCode));

    switch (res?.status) {
      case 200:
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(
          res?.data?.data || t('common.message.error'),
          errorVariant,
        );
    }
  };

  const getClassrooms = async (campusCode: string) => {
    const res = await dispatch(doGetCampusClassrooms(campusCode));

    switch (res?.status) {
      case 200:
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(
          res?.data?.data || t('common.message.error'),
          errorVariant,
        );
    }
  };

  const onOpenSelectTeacher = () => {
    if (!campus) {
      return enqueueSnackbar(t('class.class.message2'), infoVariant);
    }

    setSelectTeacherVisible(true);
  };

  const calculateTuition = (
    course: string,
    lessonTime: string,
    minPrice: string,
  ) => {
    const _course: any = courses?.find(
      (item: any) => item.semesterCourseCode === Number(course),
    );
    if (!_course) return;

    const _lessonTime = Number(lessonTime);
    const _minPrice = Number(minPrice);

    const _monthTuition1 =
      _lessonTime *
      _minPrice *
      _course?.dayLessonCount *
      _course?.months[0]?.lessonCount;
    const _monthTuition2 =
      _lessonTime *
      _minPrice *
      _course?.dayLessonCount *
      _course?.months[1]?.lessonCount;
    const _monthTuition3 =
      _lessonTime *
      _minPrice *
      _course?.dayLessonCount *
      _course?.months[2]?.lessonCount;

    setMonthTuition1(String(_monthTuition1));
    setMonthTuition2(String(_monthTuition2));
    setMonthTuition3(String(_monthTuition3));
  };

  const onSetScheduleInput = (index: number, key: string, value: string) => {
    const _scheduleInput = [...scheduleInput];
    _scheduleInput[index][key] = value;
    setScheduleInput(_scheduleInput);
  };

  const onSetStartTime = (id: any, value: string) => {
    const _scheduleInput = [...scheduleInput];
    const [index, subIndex]: any = id.split("-");

    _scheduleInput[index].classes[subIndex].startTime = value;
    setScheduleInput(_scheduleInput);
  };

  const onSetScheduleClassesInput = (
    id: string,
    key: string,
    value: any,
  ) => {
    const _scheduleInput = [...scheduleInput];
    const [index, subIndex]: any = id.split("-");

    _scheduleInput[index].classes[subIndex][key] = value;
    setScheduleInput(_scheduleInput);
  };

  const onSetTeacher = (teacher: IStaff) => {
    if (teacherInputRef === 'classTeacher') {
      setTeacher(teacher);
    } else {
      const [index, subIndex]: any = teacherInputRef.id.split("-");
      const _scheduleInput = [...scheduleInput];
      _scheduleInput[index].classes[subIndex].teacher = teacher;
      setScheduleInput(_scheduleInput);
    }
  };

  const initScheduleInput = (courseCode: string) => {
    const currentCourse = courses?.find(
      (item) => item.semesterCourseCode === Number(courseCode),
    );

    const weekLectureCount = currentCourse?.weekLectureCount;
    const dayLessonCount = currentCourse?.dayLessonCount;
    const input = [];

    if (weekLectureCount && dayLessonCount) {
      for (let i = 0; i < weekLectureCount; i++) {
        const classes = [];
        for (let j = 0; j < dayLessonCount; j++) {
          classes.push({ id: `${i}-${j}`, startTime: null, classroom: '', teacher: null });
        }

        let day;
        switch (String(weekLectureCount) + i) {
          case '20':
            day = 'TU';
            break;
          case '21':
            day = 'TH';
            break;
          case '30':
            day = 'MO';
            break;
          case '31':
            day = 'WE';
            break;
          case '32':
            day = 'FR';
            break;
          case '50':
            day = 'MO';
            break;
          case '51':
            day = 'TU';
            break;
          case '52':
            day = 'WE';
            break;
          case '53':
            day = 'TH';
            break;
          case '54':
            day = 'FR';
            break;
        }

        input.push({ day, classes });
      }
    }

    setScheduleInput(input);
  };

  const isBasicInfoValidated = () => {
    if (!campus) {
      infoNotification(t('class.class.message3'));
      return false;
    }
    if (!className) {
      infoNotification(t('class.class.message5'));
      return false;
    }
    if (!semester) {
      infoNotification(t('class.class.message4'));
      return false;
    }
    if (!teacher) {
      infoNotification(t('class.class.message9'));
      return false;
    }
    if (!eduStepCode) {
      infoNotification(t('cbt.speakingRubric.1'));
      return false;
    }
    if (!course) {
      infoNotification(t('class.class.message6'));
      return false;
    }
    if (!lessonTime) {
      infoNotification(t('class.class.message7'));
      return false;
    }
    if (semesterType === REGULAR) {
      if (!minPrice) {
        infoNotification(t('class.class.message8'));
        return false;
      }
      if (!monthTuition1 || !monthTuition2 || !monthTuition3) {
        infoNotification(t('class.class.message10'));
        return false;
      }
      if (
        Number(minPrice) <= 0 ||
        Number(monthTuition1) <= 0 ||
        Number(monthTuition2) <= 0 ||
        Number(monthTuition3) <= 0
      ) {
        infoNotification(t('class.class.message11'));
        return false;
      }
    }


    return true;
  };

  const isScheduleValidated = () => {
    for (let i = 0; i < scheduleInput.length; i++) {
      const schedule = scheduleInput[i];
      if (!schedule.day) {
        infoNotification(t('class.class.message12'));
        return false;
      }
      for (let j = 0; j < schedule.classes.length; j++) {
        const classItem = schedule.classes[j];
        if (!classItem.startTime || !classItem.classroom) {
          infoNotification(
            `${t('class.class.message13')}(${schedule.day} ${j + 1}) ${t(
              'class.class.message14',
            )}`
          );
          return false;
        }
        if (!classItem.teacher) {
          infoNotification(
            `${t('class.class.message15')}(${schedule.day} ${j + 1}) ${t(
              'class.class.message16',
            )}`
          );
          return false;
        }
      }
    }

    return true;
  };

  // 신규학급 등록 함수
  const onSubmit = async () => {
    if (!isBasicInfoValidated() || !isScheduleValidated()) return;

    const tuition =
      semesterType === REGULAR
        ? [
            {
              monthSeq: 1,
              monthTuition: monthTuition1,
              lessonCount: courseInfo.weekLectureCount * 4
            },
            {
              monthSeq: 2,
              monthTuition: monthTuition2,
              lessonCount: courseInfo.weekLectureCount * 4
            },
            {
              monthSeq: 3,
              monthTuition: monthTuition3,
              lessonCount: courseInfo.weekLectureCount * 4
            },
          ]
        : rows.map((row: any, index: number) => {
          return {
            monthSeq: index + 1,
            monthCode: row.monthCode,
            monthTuition: row.tuition,
            startDate: row.startDate,
            endDate: row.endDate,
            lessonCount: courseInfo.weekLectureCount * 4
          }
        })

    const req: any = {
      className,
      semesterCode: semester,
      semesterCourseCode: course,
      semesterType,
      classSrtDate: srtDate,
      classEndDate: endDate,
      lessonTime,
      minPrice: minPrice || 0,
      teacherCode: teacher?.memberCode,
      tuition,
      classInfo: [],
    };

    scheduleInput.forEach((schedule: any) => {
      const classTime: any = [];
      schedule.classes.forEach((classItem: any, index: number) => {
        classTime.push({
          classTimeSeq: index + 1,
          srtTime: classItem?.startTime,
          teacherCode: classItem?.teacher?.memberCode,
          classRoomCode: classItem?.classroom,
          subjectCode: classItem?.subjectCode,
        });
      });
      req.classInfo.push({ lessonWeekday: schedule.day, classTime });
    });

    if (semesterType === REGULAR) {
      if (res?.data) {
        let semesterCoursesSubject: number[] = [];
        res.data.forEach(({ subjectCode, weekLessonCount }) => {
          for (let i = 0; i < weekLessonCount; i++) {
            semesterCoursesSubject.push(subjectCode);
          }
        });
        semesterCoursesSubject = semesterCoursesSubject.sort();
        let localSubjectCodes: number[] = [];
        req.classInfo.forEach((item1: any) => {
          item1.classTime.forEach((item2: any) => {
            localSubjectCodes.push(item2.subjectCode);
          });
        });
        localSubjectCodes = localSubjectCodes.sort();
        if (!isEqual(semesterCoursesSubject, localSubjectCodes)) {
          return infoNotification(t('class.class.5'));
        }
      }
    }
    submit(campus, req);
  };

  // 학급 수정 함수 (기본정보)
  const onSubmit1 = async () => {
    if (!isBasicInfoValidated()) return;

    let req: any = {}

    if (semesterType === REGULAR) {
      req = {
        className,
        semesterType,
        lessonTime: Number(lessonTime),
        minPrice: +minPrice,
        teacherCode: teacher.memberCode,
        tuition: [
          {
            monthSeq: 1,
            monthTuition: +monthTuition1,
          },
          {
            monthSeq: 2,
            monthTuition: +monthTuition2,
          },
          {
            monthSeq: 3,
            monthTuition: +monthTuition3,
          },
        ],
      }
    } else if (semesterType === SPECIAL_CLASS) {
      req = {
        className,
        semesterType,
        lessonTime: Number(lessonTime),
        teacherCode: teacher.memberCode,
        minPrice: +minPrice,
        classSrtDate: srtDate,
        classEndDate: endDate,
      }
    }

    submit(1, data.classCode, req);
  };

  // 학급 수정 함수 (수업시간)
  const onSubmit2 = async () => {
    if (!isScheduleValidated()) return;

    const req: IClassInfoReq2[] = [];

    scheduleInput.forEach((schedule: any) => {
      schedule.classes.forEach((classItem: any) => {
        req.push({
          classTimeCode: classItem.classTimeCode,
          classTimeSeq: classItem.classTimeSeq,
          srtTime: classItem.startTime,
          teacherCode: classItem.teacher.memberCode,
          classRoomCode: classItem.classroom,
        });
      });
    });

    submit(2, data.classCode, req);
  };

  const onSubjectUpdate = (req: IClassSubjectReq[]) => {

    if (semesterType === REGULAR) {
      if (res?.data) {
        let semesterCoursesSubject: number[] = [];
        res.data.forEach(({ subjectCode, weekLessonCount }) => {
          for (let i = 0; i < weekLessonCount; i++) {
            semesterCoursesSubject.push(subjectCode);
          }
        });
        semesterCoursesSubject = semesterCoursesSubject.sort();
        let localSubjectCodes: number[] = req.map(
          ({ subjectCode }) => subjectCode,
        );
        localSubjectCodes = localSubjectCodes.sort();
        if (!isEqual(semesterCoursesSubject, localSubjectCodes)) {
          return infoNotification(t('class.class.5'));
        }
      }
    }

    submit(3, data.classCode, req);
  };

  return (
    <>
      {(semesterLoading || courseLoading || classroomLoading) && <Loader />}

      <div className="comm-bx__between">
        <h3 className='sub-title'>{t('class.class.title2')}</h3>
      </div>
      <div className='comm-table__area rowType edit'>
      <table>
        <colgroup>
          <col width="25%" />
          <col width="25%" />
          <col width="25%" />
          <col width="25%" />
        </colgroup>
        <tbody>
        <tr>
          <th>{t('class.class.column10')}</th>
          <td>
            <input
              type="text"
              className="form-control"
              placeholder={t('common.text.input')}
              value={className}
              onChange={(e) => setClassName(e.target.value)}
            />
          </td>
          <th>{t('class.class.column26')}</th>
          <td className="flex comm-gap" style={{ flexWrap: 'nowrap' }}>
            <SemesterTypeSelect
              semesterType={semesterType}
              setSemesterType={setSemesterType}
              hasAllOption={false}
              disabled={regYn === 'Y' || Boolean(classData)}
            />
            <Select
              className="w-100"
              placeholder={t('common.button.select')}
              value={semester ? String(semester) : undefined}
              onChange={(value) => {
                getCourses(value);
                setSemester(value);
                setCourse(undefined);
                setCourseInfo(undefined);
              }}
              disabled={regYn === 'Y' || Boolean(classData)}
            >
              {filteredSemesters?.map((item: ISemester) => {
                return (
                  <Option
                    key={item.semesterCode}
                    value={String(item.semesterCode) || ''}
                  >
                    {item.semesterName}
                  </Option>
                );
              })}
            </Select>
          </td>
        </tr>
        <tr>
          <th>{t('class.class.column19')}</th>
          <td colSpan={3}>
            <div className="comm-btn__area">
              <Button
                className="btn-basic small mint"
                onClick={() => {
                  setTeacherInputRef('classTeacher');
                  onOpenSelectTeacher();
                }}
                text={t('common.button.select')}
              />
              <span className="v-line">{teacher?.memberName}</span>
            </div>
          </td>
        </tr>
        <tr>
          <th>{t('common.label.1')}</th>
          <td>
            <SelectEduStep
              eduStepCode={eduStepCode}
              setEduStepCode={value => {
                setEduStepCode(value);
                setCourse(undefined);
                setCourseInfo(undefined);
              }}
              placeholder={t('student.student.placeholder6')}
              width={150}
              disabled={Boolean(classData) || !Boolean(semester)}
              noDefault
            />
          </td>
          <th>{t('class.class.column11')}</th>
          <td>
            <Select
              className="w-100"
              placeholder={t('common.button.select')}
              value={course ? String(course) : null}
              onChange={(value) => {
                setCourse(value);
                initScheduleInput(value);
                if (minPrice && lessonTime) {
                  calculateTuition(value, lessonTime, minPrice);
                }
              }}
              disabled={Boolean(classData) || !eduStepCode}
            >
              {courses?.filter(item => item.eduStepCode === eduStepCode).map((item: ICourse) => {
                return (
                  <Option
                    key={item.semesterCourseCode}
                    value={String(item.semesterCourseCode) || ''}
                  >
                    {item.courseDetailName}
                  </Option>
                );
              })}
            </Select>
          </td>
        </tr>
        <tr>
          <th>{t('class.class.column12')}</th>
          <td>{courseInfo?.courseName}</td>
          <th>{t('class.class.column13')}</th>
          <td>{courseInfo?.courseSubName}</td>
        </tr>
        <tr>
          <th>{t('class.class.column14')}</th>
          <td>
            <Select
              className="w-100p"
              placeholder={t('class.class.placeholder5')}
              disabled={regYn === 'Y'}
              value={lessonTime}
              onChange={(value) => {
                setLessonTime(value);
                if (course && minPrice) {
                  calculateTuition(course, value, minPrice);
                }
              }}
            >
              {CL0002?.map((item: any) => {
                return (
                  <Option key={item.detailCode} value={item.detailCode}>
                    {item.korName}
                  </Option>
                );
              })}
            </Select>
            &nbsp;{t('class.class.text7')}
          </td>
          <th>{t('class.class.column15')}</th>
          <td>
            {courseInfo
              ? courseInfo.dayLessonCount + ` ${t('class.class.text8')}`
              : ``}
          </td>
        </tr>
        <tr>
          <th>{t('class.class.column16')}</th>
          <td colSpan={semesterType === SPECIAL_CLASS ? 3 : 1}>
            {courseInfo
              ? courseInfo.weekLectureCount + ` ${t('class.class.text2')}`
              : ``}
          </td>
          {
            semesterType === REGULAR && (
              <>
                <th>{t('class.class.column105')}</th>
                <td>
                  <input
                    type="number"
                    className="form-control"
                    placeholder={t('common.text.input')}
                    disabled={regYn === 'Y'}
                    value={minPrice}
                    onChange={(e) => {
                      setMinPrice(e.target.value);
                      if (course && lessonTime) {
                        calculateTuition(course, lessonTime, e.target.value);
                      }
                    }}
                  />
                </td>
              </>
            )
          }
        </tr>
        <tr>
          <th>{t('payment.charge.option3')}</th>
          <td>
            {courseInfo?.onlineTuition?.toLocaleString() || 0}{' '}
            {t('class.class.text6')}
          </td>
          <th>{t('payment.charge.option4')}</th>
          <td>
            {courseInfo?.alexTuition?.toLocaleString() || 0}{' '}
            {t('class.class.text6')}
          </td>
        </tr>
        {
          semesterType === REGULAR && (
            <>
              <tr>
                <th rowSpan={3}>{t('payment.charge.option2')}</th>
                <td colSpan={3}>
                  <div className="flex">
                    <span className="pt-2">
                      {t('class.class.text1')}
                      {courseInfo
                        ? `(${courseInfo.months[0]?.lessonCount}${t(
                          'class.class.text2',
                        )})`
                        : ''}
                    </span>
                    <input
                      type="number"
                      className="form-control w-150p ml-2"
                      disabled={regYn === 'Y'}
                      value={monthTuition1}
                      onChange={(e) => setMonthTuition1(e.target.value)}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td colSpan={3}>
                  <div className="flex">
                <span className="pt-2">
                  {t('class.class.text3')}
                  {courseInfo
                    ? `(${courseInfo.months[1]?.lessonCount}${t(
                      'class.class.text2',
                    )})`
                    : ''}
                </span>
                    <input
                      type="number"
                      className="form-control w-150p ml-2"
                      disabled={regYn === 'Y'}
                      value={monthTuition2}
                      onChange={(e) => setMonthTuition2(e.target.value)}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td colSpan={3}>
                  <div className="flex">
                      <span className="pt-2">
                        {t('class.class.text4')}
                        {courseInfo
                          ? `(${courseInfo.months[2]?.lessonCount}${t(
                            'class.class.text2',
                          )})`
                          : ''}
                      </span>
                    <input
                      type="number"
                      className="form-control w-150p ml-2"
                      disabled={regYn === 'Y'}
                      value={monthTuition3}
                      onChange={(e) => setMonthTuition3(e.target.value)}
                    />
                  </div>
                </td>
              </tr>
            </>
          )
        }
        {
          semesterType === SPECIAL_CLASS && (
            <>
              <tr>
                <th>{t('class.class.column111')}</th>
                <td colSpan={3}>
                  <div className="flex comm-gap justify-content-around" style={{ flexWrap: 'nowrap', alignItems: 'center' }}>
                    <DateRangePicker
                      startDate={srtDate}
                      endDate={endDate}
                      onStartDateChange={(date) => setSrtDate(date)}
                      onEndDateChange={(date) => setEndDate(date)}
                      hyphenOnly={true}
                      disabled={regYn === 'Y' || Boolean(classData)}
                    />
                    <div>{t('class.class.column112')}</div>
                    <MonthSelect
                      month={month}
                      className="w-100p"
                      setMonth={(_month) => {
                        setMonth(_month);
                        setRows(
                          Array.from({ length: _month }, (_, index) => ({
                            ...initialRowState,
                            month: String(index + 1), // 월 순번으로 구분 가능
                            startDate: null,
                            endDate: null,
                            tuition: '',
                          }))
                        );
                      }}
                      disabled={regYn === 'Y' || Boolean(classData)}
                      numberOnly
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <th rowSpan={month + 1}>{t('payment.charge.option2')}</th>
                <td colSpan={3}>
                  <ul className="flex comm-gap justify-content-around" style={{ listStyle: 'none', textAlign: 'center'}}>
                    <li className="w-100" style={{flex: 1}}>{t('program.semester.column14')}</li>
                    <li className="w-100" style={{flex: 1}}>{t('program.semester.column15')}</li>
                    <li className="w-100" style={{flex: 1}}>{t('class.class.column4')}</li>
                    <li className="w-100" style={{flex: 1}}>{t('class.class.column5')}</li>
                    <li className="w-100" style={{flex: 1}}>{t('student.student.column112')}</li>
                  </ul>
                </td>
              </tr>
              {
                rows.map((row: any, index: number) => {
                  return (
                    <tr key={index}>
                      <td colSpan={3}>
                        <ul className="flex comm-gap justify-content-around" style={{ listStyle: 'none', textAlign: 'center' }}>
                          <li style={{flex: 1}}>{index + 1}</li>
                          <li style={{flex: 1}}>
                            <MonthSelect
                              className="w-100"
                              month={row.monthCode}
                              setMonth={(_month) => {
                                const newRows = [...rows];
                                newRows[index].monthCode = _month;
                                setRows(newRows);
                              }}
                              disabled={regYn === 'Y' || Boolean(classData)}
                            />
                          </li>
                          <li style={{flex: 2}}>
                            <DateRangePicker
                              className="w-100"
                              startDate={row.startDate}
                              endDate={row.endDate}
                              onStartDateChange={(date) => {
                                const newRows = [...rows];
                                newRows[index].startDate = date;
                                setRows(newRows);
                              }}
                              onEndDateChange={(date) => {
                                const newRows = [...rows];
                                newRows[index].endDate = date;
                                setRows(newRows);
                              }}
                              disabled={regYn === 'Y' || Boolean(classData)}
                              removeLabel
                            />
                          </li>
                          <li style={{flex: 1}}>
                            <input
                              type="number"
                              className={`form-control ${regYn === 'Y' || Boolean(classData) ? 'disabled' : ''}`}
                              disabled={regYn === 'Y' || Boolean(classData)}
                              value={row.tuition}
                              onChange={(e) => {
                                const newRows = [...rows];
                                newRows[index].tuition = e.target.value;
                                setRows(newRows);
                              }}
                            />
                          </li>
                        </ul>
                      </td>
                    </tr>
                  )
                })
              }
            </>
          )
        }
        </tbody>
      </table>
      </div>
      {data ? (
        <div className="comm-bx__space">
          <div className="comm-btn__area right">
            <Button
              className="btn-basic pink"
              onClick={onSubmit1}
              text={t('common.button.save')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      ) : null}

      <ClassTimetable2
        courseInfo={courseInfo}
        scheduleInput={scheduleInput}
        onSetScheduleInput={onSetScheduleInput}
        dayList={dayList}
        onSetStartTime={onSetStartTime}
        onSetScheduleClassesInput={onSetScheduleClassesInput}
        classroomList={classroomList}
        campus={campus}
        setTeacherInputRef={setTeacherInputRef}
        onOpenSelectTeacher={onOpenSelectTeacher}
        regYn={regYn}
        isUpdate={Boolean(classData)}
        onSubjectUpdate={onSubjectUpdate}
        semester={semester}
        semesterType={semesterType}
      />
      {regYn === 'N' ? (
        <div className="comm-bx__space">
          <div className="comm-btn__area right">
            <Button
              className="btn-basic pink"
              onClick={data ? onSubmit2 : onSubmit}
              text={t('common.button.save')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      ) : null}

      {/* 강사 배정 모달 */}
      {selectTeacherVisible && (
        <SelectTeacherModal
          visible={selectTeacherVisible}
          setVisible={setSelectTeacherVisible}
          campusCode={campus}
          setTeacher={onSetTeacher}
        />
      )}
    </>
  );
};

export default ClassEditor2;
